/**
 * Утилиты для создания JSON-LD схем различных типов
 */

/**
 * Создает JSON-LD схему для веб-сайта (Organization/WebSite)
 */
export const createWebsiteSchema = (siteName, siteUrl, description, language = 'ru') => {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteName,
        url: siteUrl,
        description: description,
        inLanguage: language,
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${siteUrl}/videos?q={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
        }
    };
};

/**
 * Создает JSON-LD схему для видео (VideoObject)
 */
export const createVideoSchema = (video, siteUrl, language = 'ru') => {
    const videoUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
    const pageUrl = `${siteUrl}/video/${video.videoId}`;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: video.title,
        description: language === 'ru' 
            ? `Статистика и аналитика видеоролика ${video.title}`
            : `Statistics and analytics for video ${video.title}`,
        thumbnailUrl: video.imageWebUrl || '',
        uploadDate: video.publishedAt || new Date().toISOString(),
        contentUrl: videoUrl,
        embedUrl: `https://www.youtube.com/embed/${video.videoId}`,
        url: pageUrl,
    };

    // Добавляем размеры изображения, если доступны
    if (video.imageWidth && video.imageHeight) {
        schema.width = video.imageWidth;
        schema.height = video.imageHeight;
    }

    // Добавляем канал как автора
    if (video.channel) {
        schema.publisher = {
            '@type': 'Organization',
            name: video.channel.title,
            url: `https://www.youtube.com/channel/${video.channel.channelId}`
        };
        schema.author = {
            '@type': 'Person',
            name: video.channel.title,
            url: `https://www.youtube.com/channel/${video.channel.channelId}`
        };
    }

    // Добавляем статистику, если доступна
    if (video.statistics) {
        schema.interactionStatistic = [
            {
                '@type': 'InteractionCounter',
                interactionType: { '@type': 'WatchAction' },
                userInteractionCount: video.statistics.viewCount || 0
            },
            {
                '@type': 'InteractionCounter',
                interactionType: { '@type': 'LikeAction' },
                userInteractionCount: video.statistics.likeCount || 0
            }
        ];
    }

    return schema;
};

/**
 * Создает JSON-LD схему для страницы со списком видео (CollectionPage)
 */
export const createVideoListSchema = (siteUrl, pageTitle, description, language = 'ru') => {
    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
        description: description,
        url: siteUrl,
        inLanguage: language,
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: []
        }
    };
};

/**
 * Создает JSON-LD схему для страницы "О нас" (AboutPage)
 */
export const createAboutPageSchema = (siteUrl, pageTitle, description, language = 'ru') => {
    return {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: pageTitle,
        description: description,
        url: `${siteUrl}/about-us`,
        inLanguage: language
    };
};

/**
 * Создает JSON-LD схему для организации (Organization)
 */
export const createOrganizationSchema = (siteName, siteUrl, logoUrl, description) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteName,
        url: siteUrl,
        logo: logoUrl,
        description: description,
        sameAs: [] // Можно добавить ссылки на соцсети
    };
};

/**
 * Создает JSON-LD схему для хлебных крошек (BreadcrumbList)
 */
export const createBreadcrumbSchema = (items, siteUrl) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url ? `${siteUrl}${item.url}` : undefined
        }))
    };
};

